import jsPDF from 'jspdf';
import { supabase } from './supabase';

// Photos come off phones at 2–6 MB each and entries routinely carry 15–30 of them.
// Embedding the originals produced 50 MB+ documents that ran the browser out of memory
// mid-download (the tab dies, the apprentice thinks the photos were lost). Every image is
// downscaled and re-encoded to a modest JPEG before it goes anywhere near jsPDF.
const PDF_IMG_MAX_PX = 1400;   // longest edge — well above the ~110mm the photo box prints at
const PDF_IMG_QUALITY = 0.72;
const PDF_IMG_CONCURRENCY = 3; // decode a few at a time so phones don't run out of memory

const VIDEO_RE = /\.(mp4|mov|webm|avi|mkv)$/i;

export function isVideoPath(pathOrUrl) {
  return VIDEO_RE.test((pathOrUrl || '').split('?')[0]);
}

// Turn a blob into a downscaled JPEG data URL via canvas. Falls back to the original
// bytes if the browser can't decode it (very old iOS, exotic formats).
async function shrinkToJpegDataUrl(blob) {
  const bitmap = await createImageBitmap(blob);
  const scale = Math.min(1, PDF_IMG_MAX_PX / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  // White matte so PNGs with transparency don't print as black blocks
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();

  return canvas.toDataURL('image/jpeg', PDF_IMG_QUALITY);
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Resolve a Supabase storage path (or legacy full URL) to a print-ready JPEG data URL.
// Returns null if anything fails — caller should handle the null gracefully.
async function resolvePhotoToDataUrl(pathOrUrl) {
  if (!pathOrUrl) return null;
  if (isVideoPath(pathOrUrl)) return null; // videos can't be embedded — caller prints a note
  try {
    let fetchUrl = pathOrUrl;
    // If it's a storage path (not a full http URL), get a signed URL first
    if (!pathOrUrl.startsWith('http')) {
      const { data, error } = await supabase.storage
        .from('evidence-photos')
        .createSignedUrl(pathOrUrl, 60 * 10); // 10 min is plenty for PDF generation
      if (error || !data?.signedUrl) return null;
      fetchUrl = data.signedUrl;
    }
    // Fetch the image bytes, then downscale before embedding
    const res = await fetch(fetchUrl);
    if (!res.ok) return null;
    const blob = await res.blob();
    try {
      return await shrinkToJpegDataUrl(blob);
    } catch (e) {
      console.warn('Could not downscale photo, embedding original:', pathOrUrl, e);
      return await blobToDataUrl(blob);
    }
  } catch (e) {
    console.warn('Failed to load photo for PDF:', pathOrUrl, e);
    return null;
  }
}

// Resolve a list of photos with a small concurrency window, reporting progress as it goes.
async function resolvePhotos(paths, onProgress) {
  const out = new Array(paths.length).fill(null);
  let next = 0;
  let done = 0;

  async function worker() {
    while (next < paths.length) {
      const i = next++;
      out[i] = await resolvePhotoToDataUrl(paths[i]);
      done++;
      onProgress?.(done, paths.length);
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(PDF_IMG_CONCURRENCY, paths.length) }, worker)
  );
  return out;
}

export async function generateEvidencePDF(entry, apprenticeName, trainerName, studentNumber, unitName, onProgress) {
  const doc = new jsPDF({ unit:"mm", format:"a4" });
  const pw=210,ph=297,ml=18,mr=18,cw=pw-ml-mr;
  let y=0;

  doc.setFillColor(15,17,23); doc.rect(0,0,pw,32,"F");
  doc.setFillColor(245,166,35); doc.rect(0,30,pw,2,"F");
  doc.setFont("helvetica","bold"); doc.setFontSize(18); doc.setTextColor(255,255,255);
  doc.text("SIGNEDOFF",ml,13);
  doc.setFontSize(10); doc.setTextColor(245,166,35);
  doc.text("APPRENTICE WORKPLACE EVIDENCE RECORD",ml,22);
  doc.setFontSize(8.5); doc.setTextColor(180,180,180);
  doc.text("signedoff.com.au",ml,29);
  y=40;

  const half=cw/2-3;
  const drawField=(label,value,x,yw,w=half)=>{
    doc.setFillColor(30,34,51); doc.roundedRect(x,yw,w,12,1,1,"F");
    doc.setFont("helvetica","bold"); doc.setFontSize(7); doc.setTextColor(107,122,153);
    doc.text(label.toUpperCase(),x+4,yw+5);
    doc.setFont("helvetica","normal"); doc.setFontSize(9.5); doc.setTextColor(220,230,240);
    doc.text(String(value||"—").substring(0,60),x+4,yw+10.5);
  };

  drawField("Apprentice Name",apprenticeName,ml,y,half);
  drawField("Job Number",entry.job_number,ml+half+6,y,half); y+=16;
  drawField("Student Number",studentNumber||"—",ml,y,half);
  drawField("Vehicle / Rego",entry.vehicle_rego||"—",ml+half+6,y,half); y+=16;
  drawField("Date of Work",entry.date,ml,y,half);
  drawField("Status",(entry.status||"").toUpperCase(),ml+half+6,y,half); y+=16;
  drawField("Unit Code",entry.unit_code,ml,y,cw); y+=16;

  doc.setFillColor(30,34,51); doc.roundedRect(ml,y,cw,12,1,1,"F");
  doc.setFont("helvetica","bold"); doc.setFontSize(7); doc.setTextColor(107,122,153);
  doc.text("UNIT NAME",ml+4,y+5);
  doc.setFont("helvetica","normal"); doc.setFontSize(9.5); doc.setTextColor(220,230,240);
  doc.text(doc.splitTextToSize(unitName||"—",cw-8)[0],ml+4,y+10.5); y+=16;

  drawField("Trainer / Supervisor",trainerName||"—",ml,y,cw); y+=20;

  const drawSection=(title,body,yPos)=>{
    doc.setFont("helvetica","bold"); doc.setFontSize(9); doc.setTextColor(245,166,35);
    doc.text(title,ml,yPos+4);
    doc.setDrawColor(40,48,72); doc.line(ml,yPos+6,ml+cw,yPos+6); yPos+=10;
    doc.setFont("helvetica","normal"); doc.setFontSize(10); doc.setTextColor(20,20,20);
    const lines=doc.splitTextToSize(body||"—",cw);
    doc.text(lines,ml,yPos);
    return yPos+lines.length*5+8;
  };

  // Structured fields (new) or legacy what/why
  if(entry.fault_reason){
    y=drawSection("FAULT / REASON FOR WORK",entry.fault_reason,y);
    if(y>ph-60){doc.addPage();y=20;}
    y=drawSection("WORK PERFORMED",entry.what_doing,y);
    if(entry.tools_used){
      if(y>ph-40){doc.addPage();y=20;}
      y=drawSection("TOOLS / EQUIPMENT USED",entry.tools_used,y);
    }
    if(y>ph-40){doc.addPage();y=20;}
    y=drawSection("OUTCOME",entry.outcome||entry.why_doing,y);
  } else {
    y=drawSection("WHAT ARE YOU DOING?",entry.what_doing,y);
    if(y>ph-80){doc.addPage();y=20;}
    y=drawSection("WHY ARE YOU DOING IT?",entry.why_doing,y);
  }

  // Photos — use photo_urls array if available, otherwise legacy fields
  let allPhotoPaths=[];
  if(entry.photo_urls){
    try{allPhotoPaths=JSON.parse(entry.photo_urls);}catch{}
  }
  if(allPhotoPaths.length===0){
    allPhotoPaths=[entry.photo_fault_url,entry.photo_repair_url,entry.photo_completed_url,entry.photo_additional_url].filter(Boolean);
  }
  if(allPhotoPaths.length>0){
    // Resolve + downscale photos a few at a time, reporting progress to the caller
    const resolvedPhotos = await resolvePhotos(allPhotoPaths, onProgress);

    if(y+75>ph-30){doc.addPage();y=20;}
    doc.setFont("helvetica","bold"); doc.setFontSize(9); doc.setTextColor(245,166,35);
    doc.text(`PHOTOGRAPHIC EVIDENCE (${allPhotoPaths.length} photo${allPhotoPaths.length!==1?'s':''})`,ml,y+4);
    doc.setDrawColor(40,48,72); doc.line(ml,y+6,ml+cw,y+6); y+=12;

    // Layout: 2 per row, large photos for clear visibility
    const perRow=Math.min(allPhotoPaths.length,2);
    const imgW=cw/perRow-4,imgH=110;
    for(let i=0;i<allPhotoPaths.length;i++){
      const col=i%perRow;
      if(col===0&&i>0){ y+=imgH+18; }
      if(col===0&&y+imgH+18>ph-20){doc.addPage();y=20;}
      const px=ml+col*(imgW+4);
      doc.setFillColor(28,34,51); doc.roundedRect(px,y,imgW,imgH+12,1,1,"F");
      doc.setFont("helvetica","bold"); doc.setFontSize(9); doc.setTextColor(107,122,153);
      doc.text(isVideoPath(allPhotoPaths[i])?`VIDEO ${i+1}`:`PHOTO ${i+1}`,px+4,y+7);
      const dataUrl = resolvedPhotos[i];
      if(dataUrl){
        try{
          // Everything is re-encoded to JPEG on the way in; PNG only survives the fallback path
          const fmt = dataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
          // Fit inside the box without stretching
          const boxW=imgW-4, boxH=imgH-2;
          let dw=boxW, dh=boxH;
          try{
            const props=doc.getImageProperties(dataUrl);
            const s=Math.min(boxW/props.width, boxH/props.height);
            dw=props.width*s; dh=props.height*s;
          }catch{}
          doc.addImage(dataUrl,fmt,px+2+(boxW-dw)/2,y+10+(boxH-dh)/2,dw,dh);
        }
        catch(e){
          console.warn('addImage failed for photo',i,e);
          doc.setFontSize(10);doc.setTextColor(60,70,95);doc.text("Photo attached",px+imgW/2,y+imgH/2,{align:"center"});
        }
      } else if(isVideoPath(allPhotoPaths[i])){
        doc.setFontSize(10);doc.setTextColor(60,70,95);
        doc.text("Video evidence —",px+imgW/2,y+imgH/2-3,{align:"center"});
        doc.text("view in SignedOff",px+imgW/2,y+imgH/2+4,{align:"center"});
      } else {
        doc.setFontSize(10);doc.setTextColor(60,70,95);doc.text("Photo unavailable",px+imgW/2,y+imgH/2,{align:"center"});
      }
    }
    y+=imgH+22;
  }

  // Sign-off
  if(y+50>ph-20){doc.addPage();y=20;}
  doc.setFont("helvetica","bold"); doc.setFontSize(9); doc.setTextColor(245,166,35);
  doc.text("SIGN-OFF & REVIEW",ml,y+4);
  doc.setDrawColor(40,48,72); doc.line(ml,y+6,ml+cw,y+6); y+=12;

  if(entry.review_note){
    doc.setFont("helvetica","italic"); doc.setFontSize(10); doc.setTextColor(180,200,220);
    const rn=doc.splitTextToSize(`Trainer note: "${entry.review_note}"`,cw);
    doc.text(rn,ml,y); y+=rn.length*5+6;
  }

  const sigW=cw/2-6;
  [["Trainer / Supervisor Signature",trainerName],["RTO Assessor Signature",""]].forEach(([lbl,name],i)=>{
    const sx=ml+i*(sigW+12);
    doc.setFillColor(22,27,39); doc.roundedRect(sx,y,sigW,28,1,1,"F");
    doc.setDrawColor(40,48,72); doc.roundedRect(sx,y,sigW,28,1,1,"S");
    doc.setFont("helvetica","bold"); doc.setFontSize(7); doc.setTextColor(107,122,153);
    doc.text(lbl.toUpperCase(),sx+4,y+5);
    if(name){ doc.setFont("helvetica","normal"); doc.setFontSize(9); doc.setTextColor(200,210,230); doc.text(name,sx+4,y+13); }
    doc.setDrawColor(60,72,100); doc.line(sx+4,y+24,sx+sigW-4,y+24);
    doc.setFont("helvetica","normal"); doc.setFontSize(7); doc.setTextColor(80,95,120);
    doc.text("Date: ___________________",sx+4,y+27);
  });

  // Page numbers
  const tp=doc.internal.getNumberOfPages();
  for(let p=1;p<=tp;p++){
    doc.setPage(p);
    doc.setFillColor(15,17,23); doc.rect(0,ph-14,pw,14,"F");
    doc.setFont("helvetica","normal"); doc.setFontSize(8); doc.setTextColor(107,122,153);
    doc.text(`SignedOff  |  ${entry.date}  |  Job ${entry.job_number}`,ml,ph-5);
    doc.text(`Page ${p} of ${tp}`,pw-mr,ph-5,{align:"right"});
  }

  doc.save(`Evidence_${entry.unit_code}_${entry.job_number}_${entry.date}.pdf`);
}

/** Generate a full progress report PDF for an apprentice */
export async function generateProgressPDF(appr, { allProfiles, evidence, signoffs, workplaceSignoffs, knowledgeUnits, trainingPlans, tradeSchoolLogs, kanganLogs, levelDates, units, buildLevelsFn }) {
  const jsPDF2 = (await import('jspdf')).default;
  const doc = new jsPDF2({ unit:"mm", format:"a4" });
  const pw=210,ph=297,ml=18,mr=18,cw=pw-ml-mr;
  let y=0;
  const MONTHLY_TS_TARGET = 12;

  function fmtDate(d) {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-AU", { day:"numeric", month:"short", year:"numeric" });
  }

  function checkPage(need) {
    if (y + need > ph - 20) { doc.addPage(); y = 20; }
  }

  // ── HEADER ──
  doc.setFillColor(15,17,23); doc.rect(0,0,pw,32,"F");
  doc.setFillColor(245,166,35); doc.rect(0,30,pw,2,"F");
  doc.setFont("helvetica","bold"); doc.setFontSize(16); doc.setTextColor(255,255,255);
  doc.text("SIGNEDOFF",ml,13);
  doc.setFontSize(10); doc.setTextColor(245,166,35);
  doc.text("APPRENTICE PROGRESS REPORT",ml,22);
  doc.setFontSize(8); doc.setTextColor(180,180,180);
  doc.text(`signedoff.com.au`,ml,29);
  y=38;

  // ── FIELD BOXES: Apprentice details ──
  const half = cw/2 - 3;
  const third = cw/3 - 4;

  function drawField(label, value, x, yw, w) {
    doc.setFillColor(28,34,51); doc.roundedRect(x,yw,w,14,1,1,"F");
    doc.setFont("helvetica","bold"); doc.setFontSize(6.5); doc.setTextColor(107,122,153);
    doc.text(label.toUpperCase(),x+4,yw+5);
    doc.setFont("helvetica","normal"); doc.setFontSize(9.5); doc.setTextColor(220,230,240);
    doc.text(String(value||"—").substring(0,50),x+4,yw+11.5);
  }

  // Compute contract end date (start + 3 years)
  const contractStart = appr.start_date;
  let contractEnd = null;
  if (contractStart) {
    const d = new Date(contractStart);
    d.setFullYear(d.getFullYear() + 3);
    d.setDate(d.getDate() - 1);
    contractEnd = d.toISOString().slice(0,10);
  }

  drawField("Apprentice Name", appr.name, ml, y, half);
  drawField("Student Number", appr.student_number || "—", ml+half+6, y, half);
  y += 18;
  drawField("Contract Start", fmtDate(contractStart), ml, y, third);
  drawField("Contract End", fmtDate(contractEnd), ml+third+6, y, third);
  drawField("Report Date", fmtDate(new Date().toISOString().slice(0,10)), ml+2*(third+6), y, third);
  y += 22;

  // ── TRAINER INFO ──
  const trainer = allProfiles.find(t => t.role==="trainer" && (t.assigned_to||[]).includes(appr.id));
  if (trainer) {
    drawField("Trainer / RTO Contact", `${trainer.name}${trainer.email ? '  |  '+trainer.email : ''}`, ml, y, cw);
    y += 18;
  }

  // ── OVERALL PROGRESS OVERVIEW ──
  const apprEv = (evidence||[]).filter(e => e.apprentice_id===appr.id);
  const approvedEv = apprEv.filter(e => e.status==="approved").length;
  const pendingEv = apprEv.filter(e => e.status==="pending").length;
  const rejectedEv = apprEv.filter(e => e.status==="rejected").length;
  const kDone = (knowledgeUnits||[]).filter(k => k.apprentice_id===appr.id).length;
  const wpDone = (workplaceSignoffs||[]).filter(s => s.apprentice_id===appr.id).length;
  const levels = buildLevelsFn ? buildLevelsFn(units, trainingPlans) : [];
  const totalUnits = levels.reduce((s, l) => s + l.units.length, 0);
  const completedUnits = levels.reduce((s, l) => s + l.units.filter(u => {
    const hasK = (knowledgeUnits||[]).some(k => k.apprentice_id===appr.id && k.unit_code===u.code);
    const hasW = (workplaceSignoffs||[]).some(ws => ws.apprentice_id===appr.id && ws.unit_code===u.code);
    return hasK && hasW;
  }).length, 0);
  const unitPct = totalUnits > 0 ? Math.round((completedUnits / totalUnits) * 100) : 0;

  // Time progress
  const now = new Date();
  const startD = contractStart ? new Date(contractStart) : null;
  const endD = contractEnd ? new Date(contractEnd) : null;
  const totalDays = startD && endD ? Math.round((endD - startD) / (1000*60*60*24)) : 0;
  const elapsedDays = startD ? Math.max(0, Math.round((now - startD) / (1000*60*60*24))) : 0;
  const daysRemaining = endD ? Math.max(0, Math.round((endD - now) / (1000*60*60*24))) : 0;
  const timePct = totalDays > 0 ? Math.min(100, Math.round((elapsedDays / totalDays) * 100)) : 0;

  // Trade school
  const tsLogs = (tradeSchoolLogs||[]).filter(l => l.apprentice_id===appr.id);
  const tsTotal = Math.round(tsLogs.reduce((a,l) => a+Number(l.hours||0),0) * 100) / 100;
  const monthsElapsed = startD ? Math.max(0, (now.getFullYear()-startD.getFullYear())*12 + (now.getMonth()-startD.getMonth())) : 0;
  const tsExpected = monthsElapsed * MONTHLY_TS_TARGET;
  const tsPct = tsExpected > 0 ? Math.round((tsTotal / tsExpected) * 100) : 0;

  // Kangan
  const kLogs = (kanganLogs||[]).filter(l => l.apprentice_id===appr.id);

  // ── STAT BOXES ──
  checkPage(30);
  doc.setFont("helvetica","bold"); doc.setFontSize(10); doc.setTextColor(245,166,35);
  doc.text("OVERALL PROGRESS",ml,y+5);
  doc.setDrawColor(40,48,72); doc.line(ml,y+7,ml+cw,y+7);
  y+=12;

  const stats = [
    ["Units Complete", `${completedUnits}/${totalUnits}`, unitPct >= timePct ? [39,174,96] : unitPct >= timePct-15 ? [245,158,11] : [239,68,68]],
    ["Knowledge", String(kDone), [107,122,153]],
    ["Workplace", String(wpDone), [107,122,153]],
    ["Evidence Approved", String(approvedEv), [107,122,153]],
    ["Time Used", `${timePct}%`, [107,122,153]],
    ["Days Remaining", String(daysRemaining), daysRemaining < 180 ? [239,68,68] : daysRemaining < 365 ? [245,158,11] : [107,122,153]],
  ];
  const statW = (cw - 3*(stats.length-1)) / stats.length;
  stats.forEach(([lbl,val,col],i) => {
    const sx = ml + i*(statW+3);
    doc.setFillColor(22,27,39); doc.roundedRect(sx,y,statW,22,1,1,"F");
    doc.setFont("helvetica","bold"); doc.setFontSize(13); doc.setTextColor(col[0],col[1],col[2]);
    doc.text(val,sx+statW/2,y+12,{align:"center"});
    doc.setFont("helvetica","normal"); doc.setFontSize(6); doc.setTextColor(107,122,153);
    doc.text(lbl.toUpperCase(),sx+statW/2,y+18,{align:"center"});
  });
  y+=28;

  // ── PROGRESS BARS ──
  checkPage(30);
  function drawProgressBar(label, pct, color, x, barY, w) {
    doc.setFont("helvetica","bold"); doc.setFontSize(7.5); doc.setTextColor(60,60,60);
    doc.text(label, x, barY+4);
    const barX = x+48, barW = w-70, barH = 4;
    doc.setFillColor(220,220,220); doc.roundedRect(barX, barY+1, barW, barH, 2, 2, "F");
    if (pct > 0) {
      doc.setFillColor(color[0],color[1],color[2]); doc.roundedRect(barX, barY+1, barW*Math.min(pct,100)/100, barH, 2, 2, "F");
    }
    doc.setFont("helvetica","bold"); doc.setFontSize(8); doc.setTextColor(color[0],color[1],color[2]);
    doc.text(`${pct}%`, barX+barW+3, barY+4.5);
  }

  drawProgressBar("Unit Completion", unitPct, unitPct >= timePct ? [39,174,96] : [239,68,68], ml, y, cw);
  y+=10;
  drawProgressBar("Time Elapsed", timePct, [66,133,244], ml, y, cw);
  y+=10;
  drawProgressBar("Trade School", tsPct, tsPct >= 85 ? [39,174,96] : tsPct >= 60 ? [245,158,11] : [239,68,68], ml, y, cw);
  y+=14;

  // ── WRITTEN ASSESSMENT ──
  checkPage(60);
  doc.setFont("helvetica","bold"); doc.setFontSize(10); doc.setTextColor(245,166,35);
  doc.text("WRITTEN ASSESSMENT",ml,y+5);
  doc.setDrawColor(40,48,72); doc.line(ml,y+7,ml+cw,y+7);
  y+=12;

  // Generate the written report
  const gap = timePct - unitPct;
  let overallStatus, statusColor;
  if (!contractStart) { overallStatus = "No contract start date set — unable to assess timeline progress."; statusColor = [107,122,153]; }
  else if (gap > 30) { overallStatus = "AT RISK"; statusColor = [239,68,68]; }
  else if (gap > 15) { overallStatus = "BEHIND SCHEDULE"; statusColor = [245,158,11]; }
  else { overallStatus = "ON TRACK"; statusColor = [39,174,96]; }

  if (contractStart) {
    doc.setFont("helvetica","bold"); doc.setFontSize(9); doc.setTextColor(statusColor[0],statusColor[1],statusColor[2]);
    doc.text(`Status: ${overallStatus}`,ml,y+4);
    y+=8;
  }

  const paras = [];

  // Unit progress assessment
  if (totalUnits > 0) {
    const remaining = totalUnits - completedUnits;
    if (unitPct === 100) {
      paras.push(`${appr.name} has completed all ${totalUnits} units across all year levels. All knowledge assessments and workplace competency signoffs are finalised.`);
    } else if (gap > 30) {
      paras.push(`${appr.name} has completed ${completedUnits} of ${totalUnits} units (${unitPct}%) with ${timePct}% of the apprenticeship elapsed. This represents a significant gap of ${gap} percentage points between time used and unit progress. With ${daysRemaining} days remaining, ${remaining} units must be completed at an increased rate to meet the contract end date.`);
    } else if (gap > 15) {
      paras.push(`${appr.name} has completed ${completedUnits} of ${totalUnits} units (${unitPct}%) with ${timePct}% of the apprenticeship elapsed. Progress is slightly behind schedule. Focused effort on the remaining ${remaining} units is recommended to maintain momentum toward on-time completion.`);
    } else {
      paras.push(`${appr.name} has completed ${completedUnits} of ${totalUnits} units (${unitPct}%) with ${timePct}% of the apprenticeship elapsed. Progress is tracking well against the contract timeline with ${daysRemaining} days remaining.`);
    }
  }

  // Knowledge vs evidence vs workplace breakdown
  const knowledgeOnly = levels.reduce((s,l) => s + l.units.filter(u => {
    const hasK = (knowledgeUnits||[]).some(k => k.apprentice_id===appr.id && k.unit_code===u.code);
    const hasW = (workplaceSignoffs||[]).some(ws => ws.apprentice_id===appr.id && ws.unit_code===u.code);
    return hasK && !hasW;
  }).length, 0);
  const workplaceOnly = levels.reduce((s,l) => s + l.units.filter(u => {
    const hasK = (knowledgeUnits||[]).some(k => k.apprentice_id===appr.id && k.unit_code===u.code);
    const hasW = (workplaceSignoffs||[]).some(ws => ws.apprentice_id===appr.id && ws.unit_code===u.code);
    return !hasK && hasW;
  }).length, 0);
  const neitherDone = totalUnits - completedUnits - knowledgeOnly - workplaceOnly;

  if (totalUnits > 0 && completedUnits < totalUnits) {
    let breakdown = `Breakdown of outstanding units: `;
    const parts = [];
    if (knowledgeOnly > 0) parts.push(`${knowledgeOnly} unit${knowledgeOnly !== 1 ? 's' : ''} with knowledge passed but awaiting workplace assessment`);
    if (workplaceOnly > 0) parts.push(`${workplaceOnly} unit${workplaceOnly !== 1 ? 's' : ''} with workplace assessment but awaiting knowledge signoff`);
    if (neitherDone > 0) parts.push(`${neitherDone} unit${neitherDone !== 1 ? 's' : ''} with no signoffs yet`);
    breakdown += parts.join("; ") + ".";
    paras.push(breakdown);
  }

  // Evidence activity
  const thirtyDaysAgo = new Date(now.getTime() - 30*24*60*60*1000);
  const recentEv = apprEv.filter(e => new Date(e.created_at||e.date) > thirtyDaysAgo).length;
  if (recentEv === 0 && approvedEv + pendingEv > 0) {
    paras.push(`No evidence has been submitted in the last 30 days. Regular evidence uploads are essential to demonstrate ongoing workplace learning and should be prioritised.`);
  } else if (recentEv === 0) {
    paras.push(`No workplace evidence has been submitted yet. Evidence collection should commence immediately to build the portfolio required for competency signoff.`);
  } else {
    paras.push(`${recentEv} evidence ${recentEv === 1 ? 'entry has' : 'entries have'} been submitted in the last 30 days (${approvedEv} approved, ${pendingEv} pending review, ${rejectedEv} returned). ${recentEv >= 5 ? 'Evidence submission rate is good.' : 'Increasing the frequency of evidence submissions would strengthen the portfolio.'}`);
  }

  // Trade school hours
  if (tsExpected > 0) {
    if (tsPct >= 85) {
      paras.push(`Trade school attendance is on target with ${tsTotal} hours logged against an expected ${tsExpected} hours (${tsPct}% of target). ${tsLogs.length} sessions have been recorded.`);
    } else if (tsPct >= 60) {
      paras.push(`Trade school attendance is slightly behind target with ${tsTotal} hours logged against an expected ${tsExpected} hours (${tsPct}% of target). Additional attendance is recommended to meet the required hours.`);
    } else {
      paras.push(`Trade school attendance requires urgent attention. Only ${tsTotal} hours have been logged against an expected ${tsExpected} hours (${tsPct}% of target). This represents a significant shortfall that must be addressed.`);
    }
  } else {
    paras.push(`Trade school hours logged: ${tsTotal}. ${tsLogs.length} session${tsLogs.length !== 1 ? 's' : ''} recorded.`);
  }

  // Kangan visits
  if (kLogs.length > 0) {
    paras.push(`${kLogs.length} Kangan workplace visit${kLogs.length !== 1 ? 's have' : ' has'} been conducted. Most recent visit: ${fmtDate(kLogs.sort((a,b) => b.visit_date.localeCompare(a.visit_date))[0]?.visit_date)}.`);
  }

  // Render paragraphs
  doc.setFont("helvetica","normal"); doc.setFontSize(9); doc.setTextColor(30,30,30);
  for (const para of paras) {
    checkPage(20);
    const lines = doc.splitTextToSize(para, cw);
    doc.text(lines, ml, y+4);
    y += lines.length * 4.2 + 4;
  }
  y += 6;

  // ── UNIT DETAIL BY LEVEL ──
  for (const level of levels) {
    if (level.units.length === 0) continue;
    checkPage(40);

    // Year date range
    let yearRange = "";
    if (contractStart) {
      const s = new Date(contractStart);
      const ys = new Date(s); ys.setFullYear(ys.getFullYear() + (level.year - 1));
      const ye = new Date(s); ye.setFullYear(ye.getFullYear() + level.year); ye.setDate(ye.getDate() - 1);
      yearRange = `  (${fmtDate(ys.toISOString().slice(0,10))} — ${fmtDate(ye.toISOString().slice(0,10))})`;
    }

    const levelDone = level.units.filter(u => {
      const hasK = (knowledgeUnits||[]).some(k => k.apprentice_id===appr.id && k.unit_code===u.code);
      const hasW = (workplaceSignoffs||[]).some(ws => ws.apprentice_id===appr.id && ws.unit_code===u.code);
      return hasK && hasW;
    }).length;
    const levelPct = Math.round((levelDone / level.units.length) * 100);

    doc.setFont("helvetica","bold"); doc.setFontSize(10); doc.setTextColor(245,166,35);
    doc.text(`${level.label.toUpperCase()}  —  ${levelPct}%`,ml,y+5);
    doc.setFont("helvetica","normal"); doc.setFontSize(7); doc.setTextColor(107,122,153);
    doc.text(`${levelDone}/${level.units.length} complete${yearRange}`,ml,y+11);
    doc.setDrawColor(40,48,72); doc.line(ml,y+13,ml+cw,y+13);
    y+=18;

    // Column headers
    doc.setFont("helvetica","bold"); doc.setFontSize(7); doc.setTextColor(107,122,153);
    doc.text("UNIT CODE",ml+2,y+4);
    doc.text("UNIT NAME",ml+30,y+4);
    doc.text("EVIDENCE",ml+cw-72,y+4);
    doc.text("KNOWLEDGE",ml+cw-48,y+4);
    doc.text("EVIDENCE",ml+cw-27,y+4);
    doc.text("WORKPLACE",ml+cw-6,y+4,{align:"right"});
    // Sub-labels
    doc.setFontSize(5.5);
    doc.text("(approved/req)",ml+cw-72,y+8);
    doc.text("SIGNOFF",ml+cw-48,y+8);
    doc.text("SIGNOFF",ml+cw-27,y+8);
    doc.text("SIGNOFF",ml+cw-6,y+8,{align:"right"});
    y+=12;

    for (const u of level.units) {
      checkPage(10);

      // Count approved evidence for this unit
      const evCount = apprEv.filter(e => e.status==="approved" && (e.unit_code === u.code || (() => { try { return JSON.parse(e.unit_codes||"[]").includes(u.code); } catch { return false; } })())).length;
      const minEv = u.min_evidence || 0;
      const hasK = (knowledgeUnits||[]).some(k => k.apprentice_id===appr.id && k.unit_code===u.code);
      const hasSig = (signoffs||[]).some(s => s.apprentice_id===appr.id && s.unit_code===u.code);
      const hasW = (workplaceSignoffs||[]).some(s => s.apprentice_id===appr.id && s.unit_code===u.code);
      const isComplete = hasK && hasSig && hasW;

      // If evidence signoff exists but no evidence in system, treat requirement as met (pre-system evidence)
      const effectiveEvCount = (hasSig && evCount === 0 && minEv > 0) ? minEv : evCount;

      // Calculate row height based on name wrapping
      doc.setFont("helvetica","normal"); doc.setFontSize(7.5);
      const nameMaxW = cw - 105;
      const nameLines = doc.splitTextToSize(u.name || "", nameMaxW);
      const lineH = 3.5;
      const rowH = Math.max(8, nameLines.length * lineH + 3);

      checkPage(rowH + 2);

      // Row background
      if (isComplete) { doc.setFillColor(220,252,231); doc.rect(ml,y-1.5,cw,rowH,"F"); }
      else {
        const altBg = level.units.indexOf(u) % 2 === 0;
        if (altBg) { doc.setFillColor(248,248,248); doc.rect(ml,y-1.5,cw,rowH,"F"); }
      }

      // Unit code
      doc.setFont("helvetica","bold"); doc.setFontSize(7.5);
      doc.setTextColor(isComplete ? 39 : 60, isComplete ? 174 : 60, isComplete ? 96 : 60);
      doc.text(u.code,ml+2,y+4);

      // Unit name (wrapped)
      doc.setFont("helvetica","normal"); doc.setFontSize(7.5); doc.setTextColor(40,40,40);
      doc.text(nameLines,ml+30,y+4);

      // Evidence count vs required
      const evText = minEv > 0 ? `${effectiveEvCount}/${minEv}` : String(effectiveEvCount);
      const evMet = minEv > 0 ? effectiveEvCount >= minEv : effectiveEvCount > 0;
      doc.setFont("helvetica","bold"); doc.setFontSize(7.5);
      doc.setTextColor(evMet ? 39 : (minEv > 0 && evCount < minEv) ? 239 : 107, evMet ? 174 : (minEv > 0 && evCount < minEv) ? 68 : 122, evMet ? 96 : (minEv > 0 && evCount < minEv) ? 68 : 153);
      doc.text(evText,ml+cw-66,y+4);

      // Knowledge tick
      doc.setFontSize(9);
      doc.setTextColor(hasK ? 39 : 180, hasK ? 174 : 180, hasK ? 96 : 180);
      doc.text(hasK ? "Y" : "—",ml+cw-42,y+4);

      // Evidence/signoff tick
      doc.setTextColor(hasSig ? 39 : 180, hasSig ? 174 : 180, hasSig ? 96 : 180);
      doc.text(hasSig ? "Y" : "—",ml+cw-22,y+4);

      // Workplace tick
      doc.setTextColor(hasW ? 39 : 180, hasW ? 174 : 180, hasW ? 96 : 180);
      doc.text(hasW ? "Y" : "—",ml+cw-4,y+4);

      y+=rowH;
    }
    y+=8;
  }

  // ── EVIDENCE & WORKPLACE GUIDE APPENDIX ──
  doc.addPage();
  y = 20;
  doc.setFont("helvetica","bold"); doc.setFontSize(12); doc.setTextColor(245,166,35);
  doc.text("EVIDENCE & WORKPLACE GUIDE",ml,y+5);
  doc.setFont("helvetica","normal"); doc.setFontSize(7); doc.setTextColor(107,122,153);
  doc.text("Evidence requirements and sign-off criteria for each unit in the training plan",ml,y+12);
  doc.setDrawColor(40,48,72); doc.line(ml,y+14,ml+cw,y+14);
  y+=20;

  for (const level of levels) {
    if (level.units.length === 0) continue;

    for (const u of level.units) {
      const hasGuideContent = u.photo_guide || u.write_guide || u.evidence_requirements || u.workplace_guide;
      if (!hasGuideContent) continue;

      checkPage(50);

      // Unit header
      doc.setFillColor(15,17,23); doc.roundedRect(ml,y,cw,10,1,1,"F");
      doc.setFont("helvetica","bold"); doc.setFontSize(8); doc.setTextColor(245,166,35);
      doc.text(u.code,ml+4,y+6.5);
      doc.setFont("helvetica","normal"); doc.setFontSize(7.5); doc.setTextColor(220,230,240);
      doc.text((u.name||"").substring(0,70),ml+30,y+6.5);
      if (u.min_evidence) {
        doc.setFont("helvetica","bold"); doc.setFontSize(6.5); doc.setTextColor(245,166,35);
        doc.text(`Min: ${u.min_evidence} entries`,ml+cw-4,y+6.5,{align:"right"});
      }
      y+=14;

      // Evidence requirements
      if (u.evidence_requirements) {
        checkPage(15);
        doc.setFont("helvetica","bold"); doc.setFontSize(7); doc.setTextColor(107,122,153);
        doc.text("EVIDENCE REQUIREMENTS",ml+2,y+4);
        y+=7;
        doc.setFont("helvetica","normal"); doc.setFontSize(7.5); doc.setTextColor(50,50,50);
        const reqLines = doc.splitTextToSize(u.evidence_requirements, cw-8);
        checkPage(reqLines.length * 3.5 + 4);
        doc.text(reqLines,ml+4,y+3);
        y += reqLines.length * 3.5 + 6;
      }

      // Photo guide
      if (u.photo_guide) {
        checkPage(15);
        doc.setFont("helvetica","bold"); doc.setFontSize(7); doc.setTextColor(107,122,153);
        doc.text("WHAT TO PHOTOGRAPH",ml+2,y+4);
        y+=7;
        const photos = u.photo_guide.split(",").map(p => p.trim());
        for (const photo of photos) {
          checkPage(6);
          doc.setFont("helvetica","normal"); doc.setFontSize(7.5); doc.setTextColor(50,50,50);
          doc.text(`  \u2022  ${photo}`,ml+4,y+3);
          y+=4.5;
        }
        y+=3;
      }

      // Write guide
      if (u.write_guide) {
        checkPage(15);
        doc.setFont("helvetica","bold"); doc.setFontSize(7); doc.setTextColor(107,122,153);
        doc.text("WHAT TO WRITE",ml+2,y+4);
        y+=7;
        doc.setFont("helvetica","normal"); doc.setFontSize(7.5); doc.setTextColor(50,50,50);
        const writeLines = doc.splitTextToSize(u.write_guide, cw-8);
        checkPage(writeLines.length * 3.5 + 4);
        doc.text(writeLines,ml+4,y+3);
        y += writeLines.length * 3.5 + 6;
      }

      // Workplace guide (supervisor checklist)
      if (u.workplace_guide) {
        checkPage(15);
        doc.setFont("helvetica","bold"); doc.setFontSize(7); doc.setTextColor(107,122,153);
        doc.text("WORKPLACE SIGN-OFF CRITERIA",ml+2,y+4);
        y+=7;
        const wpItems = u.workplace_guide.split("\n").filter(l => l.trim());
        for (const item of wpItems) {
          checkPage(6);
          doc.setFont("helvetica","normal"); doc.setFontSize(7.5); doc.setTextColor(50,50,50);
          doc.text(`  \u2610  ${item.trim()}`,ml+4,y+3);
          y+=4.5;
        }
        y+=3;
      }

      // Divider between units
      doc.setDrawColor(220,220,220); doc.line(ml+10,y,ml+cw-10,y);
      y+=8;
    }
  }

  // ── TRADE SCHOOL ATTENDANCE ──
  checkPage(30);
  doc.setFont("helvetica","bold"); doc.setFontSize(10); doc.setTextColor(245,166,35);
  doc.text("TRADE SCHOOL ATTENDANCE",ml,y+5);
  doc.setDrawColor(40,48,72); doc.line(ml,y+7,ml+cw,y+7);
  y+=14;

  // Summary stats
  drawField("Total Hours", `${tsTotal}`, ml, y, third);
  drawField("Expected Hours", `${tsExpected}`, ml+third+6, y, third);
  drawField("Sessions Attended", `${tsLogs.length}`, ml+2*(third+6), y, third);
  y+=20;

  // ── KANGAN WORKPLACE VISITS ──
  if (kLogs.length > 0) {
    checkPage(30);
    doc.setFont("helvetica","bold"); doc.setFontSize(10); doc.setTextColor(245,166,35);
    doc.text(`KANGAN WORKPLACE VISITS (${kLogs.length})`,ml,y+5);
    doc.setDrawColor(40,48,72); doc.line(ml,y+7,ml+cw,y+7);
    y+=14;
    kLogs.sort((a,b) => b.visit_date.localeCompare(a.visit_date)).slice(0,10).forEach(l => {
      checkPage(10);
      doc.setFont("helvetica","normal"); doc.setFontSize(9); doc.setTextColor(30,30,30);
      doc.text(`${fmtDate(l.visit_date)}  |  ${l.trainer_name||"—"}${l.notes ? `  |  ${l.notes.substring(0,55)}` : ""}`,ml+4,y+4);
      y+=7;
    });
    y+=6;
  }

  // ── SIGN-OFF SECTION ──
  checkPage(50);
  doc.setFont("helvetica","bold"); doc.setFontSize(10); doc.setTextColor(245,166,35);
  doc.text("SIGN-OFF",ml,y+5);
  doc.setDrawColor(40,48,72); doc.line(ml,y+7,ml+cw,y+7);
  y+=14;

  const sigW = cw/2-6;
  [["Employer / Supervisor",""], ["Trainer / RTO Assessor", trainer?.name || ""]].forEach(([lbl,name],i) => {
    const sx = ml + i*(sigW+12);
    doc.setFillColor(248,248,248); doc.roundedRect(sx,y,sigW,30,1,1,"F");
    doc.setDrawColor(200,200,200); doc.roundedRect(sx,y,sigW,30,1,1,"S");
    doc.setFont("helvetica","bold"); doc.setFontSize(7); doc.setTextColor(107,122,153);
    doc.text(lbl.toUpperCase(),sx+4,y+6);
    if (name) { doc.setFont("helvetica","normal"); doc.setFontSize(9); doc.setTextColor(60,60,60); doc.text(name,sx+4,y+14); }
    doc.setDrawColor(180,180,180); doc.line(sx+4,y+22,sx+sigW-4,y+22);
    doc.setFont("helvetica","normal"); doc.setFontSize(7); doc.setTextColor(140,140,140);
    doc.text("Signature",sx+4,y+21);
    doc.text("Date: ____/____/________",sx+4,y+28);
  });

  // ── FOOTER ──
  const tp = doc.internal.getNumberOfPages();
  for (let p=1; p<=tp; p++) {
    doc.setPage(p);
    doc.setFillColor(15,17,23); doc.rect(0,ph-12,pw,12,"F");
    doc.setFont("helvetica","normal"); doc.setFontSize(7); doc.setTextColor(107,122,153);
    doc.text(`SignedOff  |  ${appr.name}  |  Generated ${new Date().toLocaleDateString("en-AU")}`,ml,ph-4);
    doc.text(`Page ${p} of ${tp}`,pw-mr,ph-4,{align:"right"});
  }

  doc.save(`ProgressReport_${appr.name.replace(/\s+/g,"_")}_${new Date().toISOString().slice(0,10)}.pdf`);
}
